import { toVector3 } from '@utils/conversion/toVector3'
import { BackSide, Color, Euler } from 'three'
import { getProxyUrl } from './proxies'
export const FIFTY_NINE_MINUTES = 59 * 60 * 1000
export const MENU_INITIAL_KEY = 'PRINCIPAL' as const
//MODES
export const MODE_IDLE = 'MODE_IDLE' as const
export const MODE_2D_ZOOM_IN = 'MODE_2D_ZOOM_IN' as const
export const MODE_2D_ZOOM_OUT = 'MODE_2D_ZOOM_OUT' as const
export const MODE_2D_PAN = 'MODE_2D_PAN' as const
export const MODE_ALZADOS = 'MODE_ALZADOS' as const
export const MODE_3D_VIEW = 'MODE_3D_VIEW' as const

/**
 * Encuadre cenital al dibujar (`applyDrawCameraFit`).
 * Vista desde arriba (cámara vertical sobre el centro del módulo).
 * El `up` de la cámara sigue el eje del módulo → sin rotar la unidad; al orbitar
 * manualmente se restaura `up` estándar para controles normales.
 *
 * Ajuste fino:
 * - Más cerca / zoom in: baja `DRAW_FIT_TOP_DOWN_PADDING`, `DRAW_FIT_MIN_HEIGHT_ABOVE_BBOX` o sube `DRAW_FIT_EFFECTIVE_FOV`.
 * - Más lejos / zoom out: sube `DRAW_FIT_TOP_DOWN_PADDING` o `DRAW_FIT_MIN_HEIGHT_ABOVE_BBOX`.
 */
export const DRAW_FIT_TOP_DOWN_PADDING = 1.68
export const DRAW_FIT_MIN_HEIGHT_ABOVE_BBOX = 3600
/** Polar inicial (rad) al pasar de vista cenital a órbita Y-up; evita gimbal lock y mantiene orientación. */
export const DRAW_FIT_MIN_POLAR_FOR_ORBIT = 0.03
/** Margen extra (mm) para cotas laterales en el encuadre. */
export const DRAW_FIT_COTA_MARGIN_MM = 780
/**
 * FOV virtual para calcular la altura de encuadre (mayor = cámara más baja/cerca, menor = más lejos).
 * La cámara real mantiene `CAMERA_3D_FOV`; solo afecta al zoom de encuadre.
 */
export const DRAW_FIT_EFFECTIVE_FOV = 32

/**
 * Solapamiento (mm) con el que se colocan los módulos kube entre sí al dibujar y
 * al arrastrar/re-snap: en vez de quedar a tope, se acercan este valor para que se
 * "monten" un poco y parezcan un sofá continuo. Sube para juntarlas más; 0 = a ras.
 */
export const KUBE_SNAP_OVERLAP_MM = 15
export const POUFF_SNAP_OVERLAP_MM = 15

/** Iluminación tipo estudio: más relleno en sombras, key estable para no quemar highlights. */
export const SCENE_LIGHT_BASE_INTENSITY = 1.15 as const
export const SCENE_LIGHT_AMBIENT_MULT = 0.52 as const
export const SCENE_LIGHT_HEMISPHERE_MULT = 0.38 as const
export const SCENE_LIGHT_KEY_MULT = 1.35 as const
export const SCENE_LIGHT_FILL_MULT = 0.72 as const
export const SCENE_LIGHT_RIM_MULT = 0.35 as const
export const SCENE_LIGHT_FOCUS_MULT = 0.5 as const
export const SCENE_SHADOW_RADIUS = 2.5 as const
export const SCENE_FLOOR_SHADOW_OPACITY = 0.18 as const
/** Mitad del frustum ortográfico de sombras (mm escena). Más bajo = sombra más nítida y compacta. */
export const SCENE_SHADOW_CAMERA_HALF_EXTENT = 4800 as const
export const SCENE_TONE_MAPPING_EXPOSURE = 1.1 as const
export const SCENE_ENVIRONMENT_INTENSITY = 0.52 as const

/**
 * @deprecated Zoom digital del presupuesto; sustituido por cámara de captura dedicada.
 * Se mantiene por paths legacy (p. ej. DimensionsHeader).
 */
export const BUDGET_SCENE_CAPTURE_ZOOM_FACTOR = 1

/** Margen relativo del frustum 3D (aire antes del recorte 2D). */
export const BUDGET_CAPTURE_FRAME_MARGIN = 1.12 as const
/**
 * Padding del recorte 2D sobre el contenido / AABB proyectado.
 * Control principal de “qué tan cerca” se ve el producto en el presupuesto.
 * Ligeramente más bajo que Atemporal ¾ para compensar la vista cenital (más aire natural).
 */
export const BUDGET_CAPTURE_CROP_PADDING = 0.1 as const
/** Opacidad del shadowMaterial de suelo solo en captura de presupuesto (escena: 0.18). */
export const BUDGET_CAPTURE_SHADOW_OPACITY = 0.08 as const
/** Softness (shadow.radius) de la key light en captura de presupuesto. */
export const BUDGET_CAPTURE_SHADOW_RADIUS = 6 as const
/** Ancho máximo en px de la imagen embebida en el presupuesto. */
export const BUDGET_CAPTURE_MAX_OUTPUT_WIDTH = 900 as const
/** Ancho máximo de visualización (preview / print HTML). */
export const BUDGET_CAPTURE_MAX_DISPLAY_WIDTH_PX = 560 as const
/**
 * Altura máxima de visualización sin deformar.
 * Evita que composiciones cenitales muy alargadas desborden una hoja A4 al imprimir.
 */
export const BUDGET_CAPTURE_MAX_DISPLAY_HEIGHT_PX = 420 as const
/** Altura máxima en impresión (mm). Deja sitio a logo, refs, tejido y precio en A4. */
export const BUDGET_CAPTURE_MAX_PRINT_HEIGHT_MM = 130 as const
/**
 * Elevación de captura (grados desde el horizonte).
 * 90 = vista cenital (como al dibujar la primera unidad).
 */
export const BUDGET_CAPTURE_VIEW_ELEVATION_DEG = 90 as const

export const MODE_WAITING_DRAWING_WALL = 'MODE_WAITING_DRAWING_WALL' as const
export const MODE_DRAGGING_WALL = 'MODE_DRAGGING_WALL' as const
export const MODE_DRAGGING_VERTEX = 'MODE_DRAGGING_VERTEX' as const
export const MODE_DRAGGING_ITEM = 'MODE_DRAGGING_ITEM' as const
export const MODE_DRAGGING_ITEM_3D = 'MODE_DRAGGING_ITEM_3D' as const
export const MODE_DRAGGING_HOLE = 'MODE_DRAGGING_HOLE' as const
export const MODE_DRAWING_WALL = 'MODE_DRAWING_WALL' as const
export const MODE_DRAWING_HOLE = 'MODE_DRAWING_HOLE' as const
export const MODE_WAITING_DRAWING_ITEM = 'MODE_WAITING_DRAWING_ITEM' as const
export const MODE_DRAWING_ITEM = 'MODE_DRAWING_ITEM' as const
export const MODE_ROTATING_ITEM = 'MODE_ROTATING_ITEM' as const
export const MODE_UPLOADING_IMAGE = 'MODE_UPLOADING_IMAGE' as const
export const MODE_FITTING_IMAGE = 'MODE_FITTING_IMAGE' as const
export const MODE_VIEWING_CATALOG = 'MODE_VIEWING_CATALOG' as const
export const MODE_CONFIGURING_PROJECT = 'MODE_CONFIGURING_PROJECT' as const
export const MODE_FORCE = 'MODE_FORCE' as const
export const MODE_DRAGGING_PARTITION = 'MODE_DRAGGING_PARTITION' as const
export const MODE_FORCE_3D = 'MODE_FORCE_3D' as const
export const MODE_FORCE_ALZADOS = 'MODE_FORCE_ALZADOS' as const
export const MODES_2D_VIEWER = [
  MODE_IDLE,
  MODE_2D_ZOOM_IN,
  MODE_2D_ZOOM_OUT,
  MODE_2D_PAN,
  MODE_WAITING_DRAWING_WALL,
  MODE_DRAGGING_WALL,
  MODE_DRAGGING_VERTEX,
  MODE_DRAGGING_ITEM,
  MODE_DRAWING_WALL,
  MODE_WAITING_DRAWING_WALL,
  MODE_WAITING_DRAWING_ITEM,
  MODE_DRAWING_HOLE,
  MODE_DRAWING_ITEM,
  MODE_DRAGGING_HOLE,
  MODE_ROTATING_ITEM,
  MODE_FORCE,
  MODE_DRAGGING_PARTITION,
]

//Thinking about it...
//https://developer.mozilla.org/it/docs/Web/JavaScript/Reference/Global_Objects/Proxy
/*
let MODE_DEF = [
  'IDLE',
  '2D_ZOOM_IN',
  '2D_ZOOM_OUT',
  '2D_PAN',
  '3D_VIEW',
  '3D_FIRST_PERSON',
  'WAITING_DRAWING_WALL',
  'DRAGGING_WALL',
  'DRAGGING_VERTEX',
  'DRAGGING_ITEM',
  'DRAGGING_HOLE',
  'DRAWING_WALL',
  'DRAWING_HOLE',
  'DRAWING_ITEM',
  'ROTATING_ITEM',
  'UPLOADING_IMAGE',
  'FITTING_IMAGE',
  'VIEWING_CATALOG',
  'CONFIGURING_PROJECT',
];

export const MODE = new Proxy( MODE_DEF, { get: (target, name) => { return target.indexOf(name) !== -1 ? name : null } } );
*/

export const MODE_SNAPPING = [
  MODE_IDLE,
  MODE_2D_ZOOM_IN,
  MODE_2D_ZOOM_OUT,
  MODE_2D_PAN,
  MODE_WAITING_DRAWING_WALL,
  MODE_DRAWING_WALL,
  MODE_DRAWING_HOLE,
  MODE_DRAWING_ITEM,
  MODE_WAITING_DRAWING_ITEM,
  MODE_DRAGGING_WALL,
  MODE_DRAGGING_VERTEX,
  MODE_DRAGGING_ITEM,
  MODE_DRAGGING_ITEM_3D,
  MODE_DRAGGING_HOLE,
  MODE_FITTING_IMAGE,
  MODE_UPLOADING_IMAGE,
  MODE_ROTATING_ITEM,
]

//UNITS
export const UNIT_MILLIMETER = 'mm' as const
export const UNIT_CENTIMETER = 'cm' as const
export const UNIT_METER = 'm' as const
export const UNIT_INCH = 'in' as const
export const UNIT_FOOT = 'ft' as const
export const UNIT_MILE = 'mi' as const
export const BASE_ZOOM = 9.4 as const // 9.4

export const UNITS_LENGTH = [
  UNIT_MILLIMETER,
  UNIT_CENTIMETER,
  UNIT_METER,
  UNIT_INCH,
  UNIT_FOOT,
  UNIT_MILE,
]

export const KEYBOARD_BUTTON_CODE = {
  DELETE: 46,
  BACKSPACE: 8,
  ESC: 27,
  Z: 90,
  ALT: 18,
  C: 67,
  V: 86,
  CTRL: 17,
  ENTER: 13,
  TAB: 9,
  UP: 38,
  DOWN: 40,
  RIGHT: 39,
  LEFT: 37,
  ARROW_KEYS: [38, 40, 37, 39],
} as const

// DOM
export const SCROLL_OFFSET = 30

// Preferencias

export const GUIA2D = 'GUIA2D' as const
export const GUIA3D = 'GUIA3D' as const
export const UNIDADMEDIDA = 'UNIDADMEDIDA' as const
export const VERANGULOS = 'VERANGULOS' as const
export const C_LISOPARED2D = 'C/LISOPARED2D' as const
export const C_LISOPARED3D = 'C/LISOPARED3D' as const
export const C_WALLASCOTA = 'C/LINEASCOTA' as const
export const C_COTA = 'C/COTA' as const
export const ALTOPARED = 'ALTOPARED' as const
export const FONDOPARED = 'FONDOPARED' as const
export const T_REJILLA2D = 'T/REJILLA2D' as const
export const T_REJILLA3D = 'T/REJILLA3D' as const
export const ZOOM2D = 'ZOOM2D' as const
export const VER90 = 'VER90' as const
export const FONTS = 'FONTS' as const
export const T_REJILLATOTAL2D = 'T/REJILLATOTAL2D' as const
export const T_REJILLATOTAL3D = 'REJILLATOTAL3D' as const

// Valores 3D
export const ENV_MAP_INTESITY = 0.3 as const
export const DARK_BLUE = '#3C476E' as const
export const GIZMO_Y_OFFSET = 8000 as const
export const GIZMO_ROTATION = Math.PI / 2
export const ITEM_SCENE_SCALE = 1000 as const
export const GIZMO_SCALE = 220 as const
export const GIZMO_SCALE_3D = 280 as const
export const GIZMO_WALL_WIDTH = 0.2 as const
/** Grosor visual del arco/flechas del gizmo 3D (fino). */
export const GIZMO_VISUAL_LINE_WIDTH_3D = 1.5 as const
/** Píxeles extra de tolerancia al raycast de Line2 (agarre sin engrosar visual). */
export const GIZMO_PICK_THRESHOLD_PX = 22 as const
/** Mínimo de ancho (px) en las franjas invisibles de pick del PivotControls (drei usa 4× visual). */
export const GIZMO_INVISIBLE_PICK_MIN_PX = 36 as const

//Valores eventos
export const DOUBLE_CLICK_THRESHOLD = 500 as const

// Valores luces
export const LIGHT_GIZMO_COLOR = ['#b7153d', '#b7153d', '#b7153d'] as const
export const SHADOW_MAP_SIZE = [1024, 1024] as const
export const DISTANCE_FACTOR = 1000 as const
export const LIGHT_MESH_HEIGHT_2D = 8000 as const
export const LIGHT_MESH_SCALE = 5000 as const
export const LIGHT_PIVOT_OFFSET = 50 as const
/** Altura mínima (mm) de luces de escena sin paredes: por encima de la cámara cenital al dibujar. */
export const KUBE_SCENE_LIGHT_MIN_HEIGHT = 15000 as const
export const BASE_LIGHT_MULTIPLIER = 500_000
//Spotlight 3d
export const SPOT_CAMERA_FAR = 10000 as const
export const SPOT_CAMERA_NEAR = 0.1 as const
export const SPOT_CAMERA_FOV = 90 as const
export const SPOT_CAMERA_FOCUS = 1 as const

//Pointlight 3d
export const POINT_CAMERA_NEAR = 0.1 as const
export const POINT_CAMERA_FAR = 10000 as const
export const POINT_SHADOW_BIAS = 0.000001 as const

//Directional 3d
export const DIRECTIONAL_SHADOW_BIAS = -0.001 as const
export const DIRECTIONAL_CAMERA_NEAR = 0.1 as const
export const DIRECTIONAL_CAMERA_FAR = 10028 as const
export const DIRECTIONAL_CAMERA_LEFT = -5028 as const
export const DIRECTIONAL_CAMERA_RIGHT = 5028 as const
export const DIRECTIONAL_CAMERA_BOTTOM = -5028 as const
export const DIRECTIONAL_CAMERA_TOP = 5028 as const

//Cubecamera
export const CUBE_CAMERA_RESOLUTION_2D = 16 as const
export const CUBE_CAMERA_RESOLUTION_3D = 64 as const
export const CUBE_CAMERA_POSITION = toVector3([0, 0, 0])

export const CUBE_CAMERA_FAR_ITEM = 5000 as const
export const CUBE_CAMERA_NEAR_ITEM = 0.1 as const
export const CUBE_CAMERA_FRAMES = 1 as const
export const CUBE_CAMERA_FRAMES_ITEM = 5 as const
export const CUBE_CAMERA_FAR = 1 as const
export const CUBE_CAMERA_FIXED_ROTATION = new Euler(0, Math.PI / 2, 0, 'XYZ')
export const ITEMS_REVERT_FIXED_ROTATION = new Euler(0, -Math.PI / 2, 0, 'XYZ')

//Area
export const AREA_DEFAULT_POSITION = toVector3([0, -1, 0])
export const AREA_DEFAULT_ROTATION = new Euler(Math.PI / 2, 0, 0)

//3D
export const SELECTED_ITEM_COLOR = new Color('#99C3FB')
export const ABSOLUTE_X_AXIS = toVector3([1, 0, 0])
export const ABSOLUTE_Y_AXIS = toVector3([0, 1, 0])
export const ABSOLUTE_Z_AXIS = toVector3([0, 0, 1])
export const ABSOLUTE_NEGATIVE_X_AXIS = toVector3([-1, 0, 0])
export const ABSOLUTE_NEGATIVE_Y_AXIS = toVector3([0, -1, 0])
export const ABSOLUTE_NEGATIVE_Z_AXIS = toVector3([0, 0, -1])

//Camera 3D
export const CAMERA_3D_NEAR = 2 as const
export const CAMERA_3D_FAR = 50000 as const
export const CAMERA_3D_FOV = 45 as const
export const CAMERA_3D_ZOOM = 1 as const
export const CONTROLS_3D_SMOOTH = 0.1 as const
export const CONTROLS_3D_MIN_DISTANCE = 1000 as const
export const CONTROLS_DOLLY_SPEED = 0.25 as const

//Camera 3D
export const CAMERA_2D_NEAR = 0.1
export const CAMERA_2D_FAR = 10000
export const CAMERA_2D_SMOOTH = 10000
export const CAMERA_2D_MIN_POLAR = 0
export const CAMERA_2D_MAX_POLAR = 0
export const PAN_ACTION = 2

//Items
export const NESTED_ITEM_BASE_MULTIPLIER = 1000

//Grid
export const GRID_FADE_DISTANCE = 100000
export const GRID_FADE_STRENGTH = 0
export const GRID_CELL_SIZE = 200
export const GRID_SECTION_SIZE = 1000
export const GRID_SECTION_COLOR = '#404040'
export const GRID_CELL_COLOR = '#404040'
export const GRID_SECTION_COLOR2 = '#e0dede'
export const GRID_CELL_COLOR2 = '#f2f2f2'
export const GRID_CELL_THICKNESS = 1.25
export const GRID_SECTION_THICKNESS = 1
export const GRID_SCALE = 1
export const GRID_SIZE = 50000
export const GRID_DETAIL = 1
export const GRID_POSITION = toVector3([0, -1.1, 0])
export const GRID_BACKGROUND_COLOR = new Color('white')
export const GRID_SIDE = BackSide

//Keycodes
export const ENTER_KEY = 'Enter'

// Drawing
export const SNAP_RANGE_DIRECTIONAL_KEY = 100
export const DEFAULT_SUGGESTED_ROTATION = Math.PI / 2

//Colisions
export const COLLISION_WALL_RANGE_THICKNESS = 0
export const COLLISION_WALL_2D = 2500

//Base walls values
export const CUT_WALL_THICKNESS = 300

export const SWAGGER_URL =
  '${(h23 | h24 | w54)api.(for2home | for2inspiration).(com | ai)}/swagger/v1/swagger.json'
export const CURRENT_SWAGGER_URL = getProxyUrl('swagger/v1/swagger.json')

export {
  VIEWPORT_BREAKPOINTS,
  VIEWPORT_LAYOUTS,
  VIEWPORT_MEDIA_QUERIES,
  getViewportLayout,
  isCompactLayout,
  isTouchLayout,
} from './constants/viewport'
export type { ViewportLayout } from './constants/viewport'